import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
    Upload, FileText, Users, Search, LogOut, Loader2, 
    Briefcase, Star, MessageSquare, Send, Trash2, Eye,
    Building, ChevronRight, X, Mail
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientPortal() {
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('upload'); // 'upload', 'jds', 'candidates'
    
    // JD Upload State
    const [isUploading, setIsUploading] = useState(false);
    const [jds, setJds] = useState([]);
    const [selectedJD, setSelectedJD] = useState(null);
    
    // Candidate Match State
    const [candidates, setCandidates] = useState([]);
    const [isMatching, setIsMatching] = useState(false);
    const [matchJobTitle, setMatchJobTitle] = useState('');
    
    // Direct JD Text Input
    const [directJDText, setDirectJDText] = useState('');
    const [directJobTitle, setDirectJobTitle] = useState('');
    
    // Message State
    const [messageModal, setMessageModal] = useState(null);
    const [messageContent, setMessageContent] = useState('');
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    
    // CV Preview State
    const [cvPreview, setCvPreview] = useState(null);

    const loadClientProfile = useCallback(async () => {
        try {
            const profile = await clientApi.getProfile();
            setClient(profile);
            await loadJDs();
        } catch (error) {
            console.error('Not authenticated:', error);
            navigate('/client/login');
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    const loadJDs = async () => {
        try {
            const result = await clientApi.listJDs();
            setJds(result.jds || []);
        } catch (error) {
            console.error('Error loading JDs:', error);
        }
    };

    useEffect(() => {
        loadClientProfile();
    }, [loadClientProfile]);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setIsUploading(true);
        try {
            const result = await clientApi.uploadJD(file);
            toast.success(`JD uploaded: ${result.job_title}`);
            await loadJDs();
            setActiveTab('jds');
        } catch (error) {
            toast.error(error.message || 'Failed to upload JD');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteJD = async (jdId) => {
        try {
            await clientApi.deleteJD(jdId);
            toast.success('JD deleted');
            await loadJDs();
            if (selectedJD?.jd_id === jdId) {
                setSelectedJD(null);
                setCandidates([]);
            }
        } catch (error) {
            toast.error('Failed to delete JD');
        }
    };

    const handleMatchCandidates = async (jd = null) => {
        setIsMatching(true);
        try {
            let result;
            if (jd) {
                result = await clientApi.matchCandidates(jd.jd_id, null, jd.job_title);
                setSelectedJD(jd);
            } else if (directJDText.trim()) {
                result = await clientApi.matchCandidates(null, directJDText, directJobTitle || 'Job Position');
            } else {
                toast.error('Please upload a JD or enter JD text');
                setIsMatching(false);
                return;
            }
            
            setCandidates(result.candidates || []);
            setMatchJobTitle(result.job_title);
            setActiveTab('candidates');
            toast.success(`Found ${result.total} matching candidates`);
        } catch (error) {
            toast.error(error.message || 'Failed to match candidates');
        } finally {
            setIsMatching(false);
        }
    };

    const handleSendMessage = async () => {
        if (!messageContent.trim() || !messageModal) return;
        
        if (!messageModal.user_id) {
            toast.error('Cannot send message - candidate ID not found');
            return;
        }
        
        setIsSendingMessage(true);
        try {
            await clientApi.messageCandidate(messageModal.user_id, messageContent);
            toast.success(`Message sent to ${messageModal.candidate_name}`);
            setMessageModal(null);
            setMessageContent('');
        } catch (error) {
            console.error('Message send error:', error);
            toast.error(error.message || 'Failed to send message');
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleLogout = async () => {
        try {
            await clientApi.logout();
            navigate('/client/login');
        } catch (error) {
            console.error('Logout error:', error);
            navigate('/client/login');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-void flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-acid animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-void text-white">
            {/* Header */}
            <header className="border-b border-border bg-void-paper">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Building className="w-6 h-6 text-cyber-purple" />
                                <h1 className="font-display text-xl font-bold">Client Portal</h1>
                            </div>
                            <Badge className="bg-cyber-purple/20 text-cyber-purple border-cyber-purple/40 font-mono text-xs">
                                {client?.company_name}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-muted-foreground text-sm font-mono">{client?.email}</span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleLogout}
                                className="text-muted-foreground hover:text-white"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 py-8">
                {/* Tab Navigation */}
                <div className="flex gap-2 mb-6">
                    <Button
                        variant={activeTab === 'upload' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('upload')}
                        className={activeTab === 'upload' 
                            ? 'bg-acid text-black font-mono' 
                            : 'border-border text-muted-foreground hover:text-white font-mono'
                        }
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload JD
                    </Button>
                    <Button
                        variant={activeTab === 'jds' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('jds')}
                        className={activeTab === 'jds' 
                            ? 'bg-acid text-black font-mono' 
                            : 'border-border text-muted-foreground hover:text-white font-mono'
                        }
                    >
                        <FileText className="w-4 h-4 mr-2" />
                        My JDs ({jds.length})
                    </Button>
                    <Button
                        variant={activeTab === 'candidates' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('candidates')}
                        className={activeTab === 'candidates' 
                            ? 'bg-acid text-black font-mono' 
                            : 'border-border text-muted-foreground hover:text-white font-mono'
                        }
                    >
                        <Users className="w-4 h-4 mr-2" />
                        Candidates ({candidates.length})
                    </Button>
                </div>

                {/* Upload JD Tab */}
                {activeTab === 'upload' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* File Upload */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader>
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <Upload className="w-5 h-5 text-acid" />
                                    Upload Job Description
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <label className="block border-2 border-dashed border-border hover:border-acid/50 rounded-lg p-12 text-center cursor-pointer transition-colors group">
                                    <input
                                        type="file"
                                        accept=".pdf,.docx,.txt"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                        disabled={isUploading}
                                    />
                                    {isUploading ? (
                                        <div>
                                            <Loader2 className="w-12 h-12 text-acid animate-spin mx-auto mb-4" />
                                            <p className="text-muted-foreground">Parsing JD...</p>
                                        </div>
                                    ) : (
                                        <>
                                            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4 group-hover:text-acid transition-colors" />
                                            <p className="text-white text-lg mb-2">Drop your JD file here</p>
                                            <p className="text-muted-foreground text-sm">PDF, DOCX, or TXT</p>
                                        </>
                                    )}
                                </label>
                            </CardContent>
                        </Card>

                        {/* Direct Text Input */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader>
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <Briefcase className="w-5 h-5 text-cyber-purple" />
                                    Or Paste JD Text
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-muted-foreground text-xs font-mono mb-2 block">
                                        Job Title
                                    </label>
                                    <Input
                                        value={directJobTitle}
                                        onChange={(e) => setDirectJobTitle(e.target.value)}
                                        placeholder="e.g., Senior Solidity Developer"
                                        className="bg-black/20 border-border focus:border-cyber-purple"
                                    />
                                </div>
                                <div>
                                    <label className="text-muted-foreground text-xs font-mono mb-2 block">
                                        Job Description
                                    </label>
                                    <Textarea
                                        value={directJDText}
                                        onChange={(e) => setDirectJDText(e.target.value)}
                                        placeholder="Paste the full job description here..."
                                        className="bg-black/20 border-border focus:border-cyber-purple min-h-[200px] font-mono text-sm"
                                    />
                                </div>
                                <Button
                                    onClick={() => handleMatchCandidates()}
                                    disabled={isMatching || !directJDText.trim()}
                                    className="w-full bg-cyber-purple text-white hover:bg-cyber-purple/80 font-mono"
                                >
                                    {isMatching ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Search className="w-4 h-4 mr-2" />
                                    )}
                                    Find Matching Candidates
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* My JDs Tab */}
                {activeTab === 'jds' && (
                    <Card className="bg-void-paper border-border">
                        <CardHeader>
                            <CardTitle className="font-display text-lg">Your Job Descriptions</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {jds.length === 0 ? (
                                <div className="text-center py-12">
                                    <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                                    <p className="text-muted-foreground">No JDs uploaded yet</p>
                                    <Button
                                        onClick={() => setActiveTab('upload')}
                                        className="mt-4 bg-acid text-black font-mono"
                                    >
                                        Upload Your First JD
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {jds.map((jd) => (
                                        <div
                                            key={jd.jd_id}
                                            className="p-4 border border-border rounded-lg hover:border-acid/50 transition-colors"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <h3 className="text-white font-medium">{jd.job_title}</h3>
                                                    <p className="text-muted-foreground text-sm font-mono">
                                                        {jd.filename} • {new Date(jd.uploaded_at).toLocaleDateString()}
                                                    </p>
                                                    {jd.parsed_skills?.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {jd.parsed_skills.slice(0, 5).map((skill, idx) => (
                                                                <Badge key={idx} variant="outline" className="text-xs border-acid/30 text-acid">
                                                                    {skill}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        onClick={() => handleMatchCandidates(jd)}
                                                        disabled={isMatching}
                                                        className="bg-acid text-black hover:bg-acid-hover font-mono text-sm"
                                                    >
                                                        {isMatching && selectedJD?.jd_id === jd.jd_id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Search className="w-4 h-4 mr-1" />
                                                                Match
                                                            </>
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteJD(jd.jd_id)}
                                                        className="text-muted-foreground hover:text-neon-red"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Candidates Tab */}
                {activeTab === 'candidates' && (
                    <Card className="bg-void-paper border-border">
                        <CardHeader>
                            <CardTitle className="font-display text-lg flex items-center justify-between">
                                <span className="flex items-center gap-2">
                                    <Users className="w-5 h-5 text-acid" />
                                    Matching Candidates
                                    {matchJobTitle && (
                                        <Badge className="bg-cyber-purple/20 text-cyber-purple border-cyber-purple/40 font-mono text-xs ml-2">
                                            {matchJobTitle}
                                        </Badge>
                                    )}
                                </span>
                                <span className="text-muted-foreground text-sm font-mono">
                                    {candidates.length} candidates
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {candidates.length === 0 ? (
                                <div className="text-center py-12">
                                    <Users className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                                    <p className="text-muted-foreground">No candidates matched yet</p>
                                    <p className="text-muted-foreground/50 text-sm mt-1">
                                        Upload a JD and click "Match" to find candidates
                                    </p>
                                </div>
                            ) : (
                                <ScrollArea className="h-[600px]">
                                    <div className="space-y-4">
                                        {candidates.map((candidate, idx) => (
                                            <div
                                                key={candidate.cv_id}
                                                className={`p-4 border rounded-lg transition-colors ${
                                                    idx < 3 
                                                        ? 'border-acid/50 bg-acid/5' 
                                                        : 'border-border hover:border-acid/30'
                                                }`}
                                            >
                                                <div className="flex items-start gap-4">
                                                    <Avatar className="w-12 h-12 border-2 border-acid">
                                                        <AvatarFallback className="bg-acid/20 text-acid font-display text-lg">
                                                            {candidate.candidate_name?.charAt(0) || '?'}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h3 className="text-white font-medium text-lg">
                                                                {candidate.candidate_name}
                                                            </h3>
                                                            {idx < 3 && (
                                                                <Badge className="bg-acid/20 text-acid border-acid/40 text-xs">
                                                                    <Star className="w-3 h-3 mr-1" />
                                                                    Top Match
                                                                </Badge>
                                                            )}
                                                            <Badge className={`text-xs ${
                                                                candidate.fit_label === 'Strong Match' 
                                                                    ? 'bg-green-500/20 text-green-400 border-green-500/40'
                                                                    : candidate.fit_label === 'Good Match'
                                                                    ? 'bg-blue-500/20 text-blue-400 border-blue-500/40'
                                                                    : 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                                                            }`}>
                                                                {candidate.fit_label}
                                                            </Badge>
                                                        </div>
                                                        
                                                        <p className="text-cyber-purple text-sm mb-2">
                                                            {candidate.primary_sector}
                                                        </p>
                                                        
                                                        <p className="text-neon-cyan/80 text-sm italic mb-2">
                                                            {candidate.match_reason}
                                                        </p>
                                                        
                                                        {/* Matched Skills with JD comparison */}
                                                        {candidate.matched_skills?.length > 0 && (
                                                            <div className="mb-3">
                                                                <p className="text-xs text-muted-foreground mb-1.5 font-mono">Matched Skills from JD:</p>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {candidate.matched_skills.slice(0, 8).map((skill, i) => (
                                                                        <Badge key={i} variant="outline" className="text-xs border-acid/30 text-acid">
                                                                            {skill}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Experience Highlights - CV to JD mapping */}
                                                        {candidate.experience_highlights?.length > 0 && (
                                                            <div className="bg-black/30 rounded-lg p-3 border border-border/50 mb-3">
                                                                <p className="text-xs text-muted-foreground mb-2 font-mono flex items-center gap-1">
                                                                    <Eye className="w-3 h-3" />
                                                                    CV Evidence Matching JD:
                                                                </p>
                                                                <div className="space-y-2">
                                                                    {candidate.experience_highlights.slice(0, 2).map((exp, i) => (
                                                                        <div key={i} className="text-xs">
                                                                            <div className="flex items-start gap-2">
                                                                                <ChevronRight className="w-3 h-3 text-acid mt-0.5 flex-shrink-0" />
                                                                                <div>
                                                                                    <p className="text-white/90 leading-relaxed">
                                                                                        "{exp.cv_experience}"
                                                                                    </p>
                                                                                    {exp.matched_skills?.length > 0 && (
                                                                                        <p className="text-acid/80 mt-1">
                                                                                            Matches JD requirements: {exp.matched_skills.join(', ')}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Skill Match Details */}
                                                        {candidate.skill_matches?.length > 0 && !candidate.experience_highlights?.length && (
                                                            <div className="bg-black/30 rounded-lg p-3 border border-border/50 mb-3">
                                                                <p className="text-xs text-muted-foreground mb-2 font-mono">
                                                                    Skill Mapping (CV → JD):
                                                                </p>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {candidate.skill_matches.slice(0, 4).map((match, i) => (
                                                                        <span key={i} className="text-xs text-white/80">
                                                                            <span className="text-neon-cyan">{match.cv_skill}</span>
                                                                            <span className="text-muted-foreground mx-1">→</span>
                                                                            <span className="text-acid">{match.jd_skill}</span>
                                                                            {i < Math.min(3, candidate.skill_matches.length - 1) && (
                                                                                <span className="text-border mx-2">|</span>
                                                                            )}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {candidate.experience_preview && !candidate.experience_highlights?.length && (
                                                            <p className="text-muted-foreground text-xs line-clamp-2">
                                                                {candidate.experience_preview}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col gap-2">
                                                        <Button
                                                            onClick={() => setCvPreview(candidate)}
                                                            variant="outline"
                                                            className="border-border text-muted-foreground hover:text-acid hover:border-acid font-mono text-sm"
                                                        >
                                                            <Eye className="w-4 h-4 mr-1" />
                                                            View CV
                                                        </Button>
                                                        <Button
                                                            onClick={() => setMessageModal(candidate)}
                                                            className="bg-cyber-purple text-white hover:bg-cyber-purple/80 font-mono text-sm"
                                                        >
                                                            <MessageSquare className="w-4 h-4 mr-1" />
                                                            Contact
                                                        </Button>
                                                        {candidate.telegram_handle && (
                                                            <a
                                                                href={`https://t.me/${candidate.telegram_handle}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center justify-center gap-1 px-3 py-2 bg-[#0088cc]/20 hover:bg-[#0088cc]/30 text-[#0088cc] rounded-md font-mono text-sm transition-colors"
                                                            >
                                                                <Send className="w-4 h-4" />
                                                                @{candidate.telegram_handle}
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            )}
                        </CardContent>
                    </Card>
                )}
            </main>

            {/* Message Modal */}
            {messageModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <Card className="bg-void-paper border-border w-full max-w-lg">
                        <CardHeader>
                            <CardTitle className="font-display text-lg flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-cyber-purple" />
                                Message {messageModal.candidate_name}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Textarea
                                value={messageContent}
                                onChange={(e) => setMessageContent(e.target.value)}
                                placeholder={`Hi ${messageModal.candidate_name?.split(' ')[0]}, I noticed your profile and would like to discuss an opportunity...`}
                                className="bg-black/20 border-border focus:border-cyber-purple min-h-[150px]"
                            />
                            <div className="flex gap-3">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setMessageModal(null);
                                        setMessageContent('');
                                    }}
                                    className="flex-1 border-border text-muted-foreground"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSendMessage}
                                    disabled={isSendingMessage || !messageContent.trim()}
                                    className="flex-1 bg-cyber-purple text-white hover:bg-cyber-purple/80"
                                >
                                    {isSendingMessage ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Send className="w-4 h-4 mr-2" />
                                    )}
                                    Send Message
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* CV Preview Modal */}
            {cvPreview && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <Card className="bg-void-paper border-border w-full max-w-3xl max-h-[90vh] flex flex-col">
                        <CardHeader className="pb-3 border-b border-border flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                    CV: {cvPreview.candidate_name || 'Candidate'}
                                </CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCvPreview(null)}
                                    className="text-muted-foreground hover:text-white"
                                >
                                    <X className="w-5 h-5" strokeWidth={1.5} />
                                </Button>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                {cvPreview.primary_sector && (
                                    <Badge className="font-mono text-xs bg-cyber-purple/10 text-cyber-purple border-cyber-purple/30 capitalize">
                                        {cvPreview.primary_sector}
                                    </Badge>
                                )}
                                {cvPreview.fit_label && (
                                    <Badge className={`text-xs ${
                                        cvPreview.fit_label === 'Strong Match' 
                                            ? 'bg-green-500/20 text-green-400 border-green-500/40'
                                            : cvPreview.fit_label === 'Good Match'
                                            ? 'bg-blue-500/20 text-blue-400 border-blue-500/40'
                                            : 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                                    }`}>
                                        {cvPreview.fit_label}
                                    </Badge>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="p-4 overflow-y-auto flex-1">
                            {/* Match Reason */}
                            {cvPreview.match_reason && (
                                <div className="mb-4 p-3 bg-acid/10 border border-acid/30 rounded-lg">
                                    <p className="text-sm text-acid italic">{cvPreview.match_reason}</p>
                                </div>
                            )}
                            
                            {/* Skills */}
                            {cvPreview.skills?.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Skills</h4>
                                    <div className="flex flex-wrap gap-1">
                                        {cvPreview.skills.map((skill, idx) => (
                                            <Badge key={idx} variant="outline" className="text-xs border-acid/30 text-acid">
                                                {skill}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Experience Highlights from match */}
                            {cvPreview.experience_highlights?.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Relevant Experience</h4>
                                    <ul className="space-y-2">
                                        {cvPreview.experience_highlights.map((exp, idx) => (
                                            <li key={idx} className="text-sm text-white/90 pl-3 border-l-2 border-acid/30">
                                                {exp.cv_experience}
                                                {exp.matched_skills?.length > 0 && (
                                                    <div className="flex gap-1 mt-1">
                                                        {exp.matched_skills.map((s, i) => (
                                                            <Badge key={i} variant="outline" className="text-[10px] border-neon-cyan/30 text-neon-cyan">
                                                                {s}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {/* Full Experience */}
                            {cvPreview.experience_preview && !cvPreview.experience_highlights?.length && (
                                <div className="mb-4">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Experience Preview</h4>
                                    <p className="text-sm text-white/80">{cvPreview.experience_preview}</p>
                                </div>
                            )}
                            
                            {/* Matched Skills */}
                            {cvPreview.matched_skills?.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Skills Matching Your JD</h4>
                                    <div className="flex flex-wrap gap-1">
                                        {cvPreview.matched_skills.map((skill, idx) => (
                                            <Badge key={idx} className="text-xs bg-green-500/20 text-green-400 border-green-500/40">
                                                {skill}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Contact Actions */}
                            <div className="mt-4 pt-4 border-t border-border">
                                <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3">Contact Candidate</h4>
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        onClick={() => { setCvPreview(null); setMessageModal(cvPreview); }}
                                        className="bg-cyber-purple text-white hover:bg-cyber-purple/80"
                                    >
                                        <MessageSquare className="w-4 h-4 mr-2" />
                                        Send Message
                                    </Button>
                                    {cvPreview.email && (
                                        <a href={`mailto:${cvPreview.email}`} className="inline-flex items-center gap-2 px-4 py-2 bg-acid/20 hover:bg-acid/30 text-acid rounded-md transition-colors">
                                            <Mail className="w-4 h-4" /> Email
                                        </a>
                                    )}
                                    {cvPreview.telegram_handle && (
                                        <a href={`https://t.me/${cvPreview.telegram_handle}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0088cc]/20 hover:bg-[#0088cc]/30 text-[#0088cc] rounded-md transition-colors">
                                            <Send className="w-4 h-4" /> @{cvPreview.telegram_handle}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
